import {JSONPath} from 'jsonpath-plus';
import _ from 'lodash';

export type FilterMode = 'none' | 'jsonpath' | 'substring';

export type SectionFilterState = {
  data: unknown;
  mode: FilterMode;
  filterError?: string;
};

/**
 * Applies JSONPath filtering to the provided data, falling back to substring matching when the
 * query cannot be parsed or doesn't look like JSONPath.
 *
 * Args:
 *   data: The original object shown in the viewer.
 *   query: The user-supplied string which may be a JSONPath expression or partial text.
 *
 * Returns:
 *   A promise that resolves to the filtered view state, including error context when JSONPath fails.
 *
 * Examples:
 *   await filterDataWithQuery({foo: 'bar'}, '$.foo');
 *   await filterDataWithQuery({foo: 'bar'}, 'ba');
 */
export const filterDataWithQuery = (
  data: unknown,
  query: string
): SectionFilterState => {
  const trimmed = query.trim();
  if (!trimmed) {
    return {data, mode: 'none'};
  }

  // If it doesn't look like JSONPath, skip straight to substring search
  if (!isLikelyJSONPath(trimmed)) {
    const substringData = filterDataBySubstring(data, trimmed);
    return {
      data: substringData,
      mode: 'substring',
    };
  }

  // Try JSONPath for queries that look like JSONPath expressions
  try {
    const result = JSONPath({path: trimmed, json: data as any, wrap: false});
    return {data: result, mode: 'jsonpath'};
  } catch (err) {
    // JSONPath failed, fall back to substring search
    const substringData = filterDataBySubstring(data, trimmed);
    return {
      data: substringData,
      mode: 'substring',
      filterError: err instanceof Error ? err.message : String(err),
    };
  }
};

/**
 * Converts a path segment to valid JSONPath notation, escaping strings as needed.
 *
 * Args:
 *   segment: The current segment from the object path, either string or number.
 *
 * Returns:
 *   A JSONPath-compatible fragment beginning with either dot or bracket notation.
 *
 * Examples:
 *   toJSONPathSegment('foo');
 *   toJSONPathSegment('key.with.dots');
 */
export const toJSONPathSegment = (segment: string | number): string => {
  if (typeof segment === 'number') {
    return `[${segment}]`;
  }
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(segment)) {
    return `.${segment}`;
  }
  const escaped = segment.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return `['${escaped}']`;
};

/**
 * Checks if a query string looks like a JSONPath expression.
 *
 * Args:
 *   query: The user-supplied query string.
 *
 * Returns:
 *   True if the query appears to be a JSONPath expression.
 *
 * Examples:
 *   isLikelyJSONPath('$.foo'); // true
 *   isLikelyJSONPath('$[0]'); // true
 *   isLikelyJSONPath('choices'); // false
 */
const isLikelyJSONPath = (query: string): boolean => {
  const trimmed = query.trim();
  // JSONPath expressions typically start with $ or contain JSONPath operators
  return (
    trimmed.startsWith('$') ||
    trimmed.startsWith('@') ||
    /\[['"\d]/.test(trimmed) || // Contains array/object accessors like [0] or ['key']
    /\.\w+/.test(trimmed) // Contains dot notation like .property
  );
};

/**
 * Recursively filters data by substring, retaining only branches that contain the term.
 *
 * Args:
 *   data: The original object to traverse.
 *   query: Lower-cased substring to match against keys and primitive values.
 *
 * Returns:
 *   The filtered structure or an empty object when no matches are found.
 *
 * Examples:
 *   filterDataBySubstring({foo: 'bar', baz: 'qux'}, 'ba');
 *   filterDataBySubstring({outer: {inner: 'value'}}, 'value');
 */
const filterDataBySubstring = (data: unknown, query: string): unknown => {
  const lowered = query.toLowerCase();
  const filtered = filterNodeBySubstring(data, lowered);
  if (filtered == null) {
    return {};
  }
  return filtered;
};

/**
 * Returns a node when its key, primitive value, or descendants contain the match term.
 *
 * Args:
 *   value: The current branch in the traversal.
 *   query: Lower-cased substring used for matching.
 *   key: Optional key name to test for matches alongside the value.
 *
 * Returns:
 *   The retained value (possibly pruned) or undefined when no match exists.
 *
 * Examples:
 *   filterNodeBySubstring({foo: 'bar'}, 'foo', 'foo');
 */
const filterNodeBySubstring = (
  value: unknown,
  query: string,
  key?: string
): unknown => {
  if (key && key.toLowerCase().includes(query)) {
    return clonePreservingType(value);
  }

  if (value == null) {
    return stringMatches(value, query) ? value : undefined;
  }

  if (Array.isArray(value)) {
    const filteredArray = value
      .map(item => filterNodeBySubstring(item, query))
      .filter(item => item !== undefined);
    return filteredArray.length > 0 ? filteredArray : undefined;
  }

  if (_.isPlainObject(value)) {
    const entries = Object.entries(value as Record<string, any>).reduce<
      Record<string, any>
    >((acc, [childKey, childValue]) => {
      const filteredChild = filterNodeBySubstring(childValue, query, childKey);
      if (filteredChild !== undefined) {
        acc[childKey] = filteredChild;
      }
      return acc;
    }, {});
    if (Object.keys(entries).length > 0) {
      return entries;
    }
    return stringMatches(value, query) ? clonePreservingType(value) : undefined;
  }

  return stringMatches(value, query) ? value : undefined;
};

/**
 * Safely clones primitive and structured values so substring matches do not mutate state.
 *
 * Args:
 *   value: The input value to clone.
 *
 * Returns:
 *   A cloned representation preserving arrays and objects.
 *
 * Examples:
 *   clonePreservingType({foo: 'bar'});
 */
const clonePreservingType = (value: any): any => {
  if (value == null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(item => clonePreservingType(item));
  }
  return Object.entries(value).reduce<Record<string, any>>((acc, [k, v]) => {
    acc[k] = clonePreservingType(v);
    return acc;
  }, {});
};

/**
 * Determines if the supplied value contains the lowercase query substring.
 *
 * Args:
 *   value: The primitive or structured value to stringify.
 *   query: Lower-cased substring to search for.
 *
 * Returns:
 *   True when the stringified value contains the query.
 *
 * Examples:
 *   stringMatches('Example', 'exam');
 */
const stringMatches = (value: any, query: string): boolean => {
  const serialized = stringifyForSearch(value);
  return serialized.includes(query);
};

/**
 * Serializes values for substring comparison in a deterministic, lowercase way.
 *
 * Args:
 *   value: Any value that needs to be converted to a string for searching.
 *
 * Returns:
 *   A lowercase string representation of the input.
 *
 * Examples:
 *   stringifyForSearch({foo: 'Bar'});
 */
const stringifyForSearch = (value: any): string => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value.toLowerCase();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).toLowerCase();
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value).toLowerCase();
    } catch (err) {
      return '';
    }
  }
  return String(value).toLowerCase();
};
